import React from 'react';
import type IViewPortData from './ViewPortData';
import ViewportContext from './ViewPortContext';
import { ViewPort } from './ViewPort';

/** Identify desktop sizes
 * Below is a range of resolutions and corresponding screen sizes
 *
 * STANDARD SIZES - without extensive:
 * 992 +         : desktop
 * 620 - 992     : tab
 * 0 - 620       : mobile
 *
 * STANDARD sizes - with extensive = true:
 * 1440 +        : desktopBig
 * 1280 - 1440   : desktop
 * 992 - 1280    : desktopSmall
 * 800 - 992     : tabBig
 * 620 - 800     : tabSmall
 * 0 - 620       : mobile
 *
 */
const DESKTOP_BIG_MIN_WIDTH = 1440;
const DESKTOP_SMALL_MAX_WIDTH = 1280;

const TAB_BIG_MAX_WIDTH = 992;
const TAB_SMALL_MAX_WIDTH = 800;

/** STANDARD tab and mobile sizes */
const TAB_MAX_WIDTH = 992;
const MOBILE_MAX_WIDTH = 620;

/**
 * useViewport hook deduces the current system based on viewport context.
 * @returns type of screen - mobile, tab, desktop
 */

const useViewport = (extensive?: boolean) => {
  const data: IViewPortData = React.useContext(ViewportContext);
  let screen = ViewPort.desktop;

  // Basic Resolutions - desktop, tab, mobile
  if (data.width <= TAB_MAX_WIDTH) {
    screen = ViewPort.tab;
  }

  if (data.width <= MOBILE_MAX_WIDTH) {
    screen = ViewPort.mobile;
  }

  // Extended Resolutions - desktopBig, desktopSmall, tabBig, tabSmall, mobile
  if (extensive) {
    if (data.width > DESKTOP_BIG_MIN_WIDTH) {
      screen = ViewPort.desktopBig;
    }
    if (data.width > TAB_BIG_MAX_WIDTH && data.width <= DESKTOP_SMALL_MAX_WIDTH) {
      screen = ViewPort.desktopSmall;
    }
    if (data.width > TAB_SMALL_MAX_WIDTH && data.width <= TAB_BIG_MAX_WIDTH) {
      screen = ViewPort.tabBig;
    }
    if (data.width > MOBILE_MAX_WIDTH && data.width <= TAB_SMALL_MAX_WIDTH) {
      screen = ViewPort.tabSmall;
    }
  }

  return screen;
};

export default useViewport;
